// components/Student/Modals/AttendanceModal.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ChevronDown,
  AlertCircle, 
  TrendingUp, 
  Clock,
  CheckCircle2,
  XCircle,
  LayoutGrid,
  List,
  RefreshCw,
  Target,
  Info,
  BookOpen,
  BarChart3,
  Calendar,
  GraduationCap
} from 'lucide-react';
import moment from 'moment';
import api from '../../api';

const AttendanceModal = ({ isOpen, onClose, userId, tenantId, title = "Attendance" }) => {
  // State Management
  const [activeTab, setActiveTab] = useState('overview');
  const [attendanceData, setAttendanceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [viewMode, setViewMode] = useState('cards');

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const subdomain = window.location.hostname.split('.')[0];
      const response = await api.post('/api/student/attendance', {
        userId,
        tenantId,
        subdomain,
        limit: 200,
        offset: 0
      });
      setAttendanceData(response.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to sync records');
    } finally {
      setLoading(false);
    }
  }, [userId, tenantId]);

  useEffect(() => {
    if (isOpen && userId && tenantId) fetchAttendance();
  }, [isOpen, userId, tenantId, fetchAttendance]);

  const analytics = useMemo(() => {
    if (!attendanceData?.subjectWise) return null;
    const subjects = attendanceData.subjectWise;
    const totalPresent = subjects.reduce((sum, s) => sum + (s.present || 0), 0);
    const totalAbsent = subjects.reduce((sum, s) => sum + (s.absent || 0), 0);
    const totalSessions = totalPresent + totalAbsent;
    const overallAttendance = totalSessions > 0 ? Math.round((totalPresent / totalSessions) * 100) : 0;
    
    return {
      totalPresent,
      totalAbsent,
      totalSessions,
      overallAttendance,
      subjectsNeedingAttention: subjects.filter(s => s.attendancePercent < 75).length,
      criticalSubjects: subjects.filter(s => s.attendancePercent < 60).length,
      bestSubject: subjects.reduce((best, current) => 
        current.attendancePercent > (best?.attendancePercent || 0) ? current : best, subjects[0]),
      requiredDaysBySubject: subjects.map(subject => {
        if (subject.attendancePercent >= 75) return { ...subject, neededDays: 0 };
        const totalNeeded = Math.ceil((0.75 * (subject.totalSessions + 5) - subject.present));
        return { ...subject, neededDays: Math.max(0, totalNeeded) };
      })
    };
  }, [attendanceData]);

  if (!isOpen) return null;

  const subjectWise = Array.isArray(attendanceData?.subjectWise) ? attendanceData.subjectWise : [];

  const getStatusConfig = (pct) => {
    if (pct >= 75) return { 
      color: 'text-emerald-600', 
      bg: 'bg-emerald-50', 
      bar: 'bg-emerald-500', 
      label: 'Good'
    };
    if (pct >= 60) return { 
      color: 'text-amber-600', 
      bg: 'bg-amber-50', 
      bar: 'bg-amber-500', 
      label: 'Risk'
    };
    return { 
      color: 'text-rose-600', 
      bg: 'bg-rose-50', 
      bar: 'bg-rose-500', 
      label: 'Critical'
    };
  };

  const getAttendanceColor = (pct) => {
    if (pct >= 75) return 'text-emerald-600';
    if (pct >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white w-full max-w-4xl h-[95vh] sm:h-auto sm:max-h-[90vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden animate-slide-up">
        
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-700 px-4 py-3 sm:px-5 sm:py-4 text-white flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="bg-white/20 p-1.5 rounded-lg">
              <GraduationCap className="w-4 h-4 sm:w-5 sm:h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold leading-tight">{title}</h2>
              <div className="flex items-center gap-1 text-[10px] sm:text-xs text-indigo-100">
                <Clock className="w-2.5 h-2.5" />
                <span>{moment().format('MMM DD, h:mm A')}</span>
              </div>
            </div>
          </div>
          <button 
            onClick={onClose} 
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        {/* Compact Stats Bar */}
        {analytics && (
          <div className="grid grid-cols-4 gap-1.5 px-3 py-2 bg-gray-50/80 border-b border-gray-100">
            <div className="bg-white rounded-lg px-2 py-1.5 text-center shadow-sm">
              <p className="text-[8px] font-bold text-gray-400 uppercase tracking-wider">Avg</p>
              <p className={`text-sm font-black ${getAttendanceColor(analytics.overallAttendance)}`}>
                {analytics.overallAttendance}%
              </p>
            </div>
            <div className="bg-white rounded-lg px-2 py-1.5 text-center shadow-sm">
              <p className="text-[8px] font-bold text-gray-400 uppercase tracking-wider">Present</p>
              <p className="text-sm font-black text-emerald-600">{analytics.totalPresent}</p>
            </div>
            <div className="bg-white rounded-lg px-2 py-1.5 text-center shadow-sm">
              <p className="text-[8px] font-bold text-gray-400 uppercase tracking-wider">Absent</p>
              <p className="text-sm font-black text-rose-600">{analytics.totalAbsent}</p>
            </div>
            <div className="bg-white rounded-lg px-2 py-1.5 text-center shadow-sm">
              <p className="text-[8px] font-bold text-gray-400 uppercase tracking-wider">Subjects</p>
              <p className="text-sm font-black text-indigo-600">{subjectWise.length}</p>
            </div>
          </div>
        )}

        {/* Compact Tab Navigation */}
        <div className="px-3 sm:px-5 flex items-center justify-between border-b border-gray-200 bg-white">
          <div className="flex gap-3 sm:gap-5">
            {['overview', 'subjects'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-2.5 sm:py-3 text-[11px] sm:text-sm font-semibold transition-all relative ${
                  activeTab === tab ? 'text-indigo-600' : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  {tab === 'overview' ? <BarChart3 className="w-3.5 h-3.5" /> : <BookOpen className="w-3.5 h-3.5" />}
                  {tab === 'overview' ? 'Overview' : 'Subjects'}
                </span>
                {activeTab === tab && (
                  <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-full" />
                )}
              </button>
            ))}
          </div>
          <button 
            onClick={fetchAttendance} 
            className="p-1.5 text-gray-400 hover:text-indigo-600 transition-colors"
            disabled={loading}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto bg-gray-50/50 p-3 sm:p-4">
          {loading ? (
            <div className="h-48 flex flex-col items-center justify-center space-y-3">
              <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-gray-500 font-medium">Loading...</p>
            </div>
          ) : error ? (
            <div className="bg-rose-50 p-4 rounded-xl text-center border border-rose-200">
              <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
              <p className="text-sm text-rose-800 font-semibold mb-3">{error}</p>
              <button 
                onClick={fetchAttendance} 
                className="bg-rose-600 text-white px-4 py-1.5 rounded-lg text-sm font-semibold hover:bg-rose-700 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {activeTab === 'overview' && analytics && (
                <div className="space-y-3">
                  {/* Compact Goal Alert */}
                  {analytics.requiredDaysBySubject.filter(s => s.neededDays > 0).length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3">
                      <div className="flex items-start gap-2">
                        <Target className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-amber-800">Need attention</p>
                          {analytics.requiredDaysBySubject.filter(s => s.neededDays > 0).slice(0, 2).map(s => (
                            <p key={s.subjectId} className="text-[11px] text-amber-700 mt-0.5">
                              {s.subjectName}: <span className="font-bold">{s.neededDays}</span> more classes
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Compact Stats Grid */}
                  <div className="grid grid-cols-2 gap-2.5">
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        Best
                      </p>
                      <p className="text-sm font-bold text-gray-800 truncate">{analytics.bestSubject?.subjectName || 'N/A'}</p>
                      <span className="text-xs font-bold text-emerald-600">{analytics.bestSubject?.attendancePercent || 0}%</span>
                    </div>
                    
                    <div className="bg-white rounded-xl p-3 shadow-sm border border-gray-100">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Critical
                      </p>
                      <p className="text-sm font-bold text-gray-800">{analytics.criticalSubjects}</p>
                      <span className="text-xs text-gray-500">below 60%</span>
                    </div>
                  </div>

                  {/* Compact Info */}
                  <div className="bg-white rounded-xl px-3 py-2 shadow-sm border border-gray-100 flex items-center gap-2">
                    <Info className="w-3.5 h-3.5 text-indigo-500 flex-shrink-0" />
                    <p className="text-[10px] text-gray-600">
                      Need <span className="font-bold text-gray-800">75%</span> attendance for exams
                    </p>
                  </div>
                </div>
              )}

              {activeTab === 'subjects' && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="font-bold text-gray-800 text-sm">All Subjects</h3>
                    <div className="flex bg-gray-200/80 p-0.5 rounded-lg">
                      <button 
                        onClick={() => setViewMode('cards')} 
                        className={`p-1 rounded-md transition-all ${
                          viewMode === 'cards' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500'
                        }`}
                      >
                        <LayoutGrid className="w-3.5 h-3.5" />
                      </button>
                      <button 
                        onClick={() => setViewMode('table')} 
                        className={`p-1 rounded-md transition-all ${
                          viewMode === 'table' ? 'bg-white shadow-sm text-indigo-600' : 'text-gray-500'
                        }`}
                      >
                        <List className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>

                  {subjectWise.length === 0 ? (
                    <div className="bg-white rounded-xl p-6 text-center border border-gray-200">
                      <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-xs text-gray-500">No subjects found</p>
                    </div>
                  ) : (
                    <div className={viewMode === 'cards' ? "grid grid-cols-1 gap-2.5" : "space-y-1.5"}>
                      {subjectWise.map(subject => {
                        const status = getStatusConfig(subject.attendancePercent);
                        const isExpanded = expandedSubject === subject.subjectId;
                        
                        return viewMode === 'cards' ? (
                          <div 
                            key={subject.subjectId} 
                            className={`bg-white rounded-xl border transition-all ${
                              isExpanded ? 'border-indigo-300 shadow-md' : 'border-gray-200 shadow-sm'
                            }`}
                          >
                            <div 
                              className="p-3 cursor-pointer"
                              onClick={() => setExpandedSubject(isExpanded ? null : subject.subjectId)}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1 min-w-0">
                                  <h4 className="font-bold text-gray-800 text-sm truncate">{subject.subjectName}</h4>
                                  <p className="text-[10px] text-gray-400 font-medium">{subject.subjectCode}</p>
                                </div>
                                <div className={`px-2 py-0.5 rounded-lg text-[9px] font-bold ${status.bg} ${status.color}`}>
                                  {status.label}
                                </div>
                              </div>

                              <div className="flex items-center justify-between mt-2">
                                <div>
                                  <p className="text-lg font-black text-gray-900">
                                    {subject.attendancePercent}
                                    <span className="text-xs font-bold text-gray-400">%</span>
                                  </p>
                                  <p className="text-[10px] text-gray-400 font-medium">
                                    {subject.present}/{subject.totalSessions}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="w-20 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                                    <div 
                                      className={`h-full ${status.bar} transition-all duration-700`} 
                                      style={{ width: `${subject.attendancePercent}%` }}
                                    />
                                  </div>
                                  <ChevronDown className={`w-4 h-4 text-gray-300 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                                </div>
                              </div>
                            </div>

                            {isExpanded && (
                              <div className="bg-gray-50 p-3 border-t border-gray-100 rounded-b-xl">
                                <p className="text-[9px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1.5 mb-2">
                                  <Calendar className="w-3 h-3" />
                                  Recent
                                </p>
                                <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1 custom-scrollbar">
                                  {subject.sessionDetails?.slice(0, 3).map((session, i) => (
                                    <div key={i} className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-100">
                                      <div className="flex items-center gap-2">
                                        <div className={`p-1 rounded-lg ${
                                          session.status === 'present' ? 'bg-emerald-100 text-emerald-600' : 'bg-rose-100 text-rose-600'
                                        }`}>
                                          {session.status === 'present' ? 
                                            <CheckCircle2 className="w-3 h-3" /> : 
                                            <XCircle className="w-3 h-3" />
                                          }
                                        </div>
                                        <span className="text-xs font-medium text-gray-700">
                                          {moment(session.sessionDate).format('MMM DD')}
                                        </span>
                                      </div>
                                      <span className={`text-[8px] font-bold uppercase px-1.5 py-0.5 rounded ${
                                        session.status === 'present' ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
                                      }`}>
                                        {session.status}
                                      </span>
                                    </div>
                                  ))}
                                  {subject.sessionDetails?.length > 3 && (
                                    <p className="text-[9px] text-gray-400 text-center">+{subject.sessionDetails.length - 3} more</p>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <div key={subject.subjectId} className="bg-white p-2.5 rounded-lg border border-gray-200 flex items-center justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <h4 className="font-bold text-gray-800 text-xs truncate">{subject.subjectName}</h4>
                              <p className="text-[9px] text-gray-400">{subject.subjectCode}</p>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className={`text-xs font-bold ${status.color}`}>{subject.attendancePercent}%</p>
                                <p className="text-[8px] text-gray-400">{subject.present}/{subject.totalSessions}</p>
                              </div>
                              <div className={`w-1 h-6 rounded-full ${status.bar}`} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* Compact Footer */}
        <div className="bg-white px-3 py-2.5 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-1.5 bg-gray-900 text-white rounded-lg font-semibold hover:bg-gray-800 transition-colors active:scale-95 text-xs sm:text-sm"
          >
            Close
          </button>
        </div>
      </div>

      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
        .custom-scrollbar::-webkit-scrollbar {
          width: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e2e8f0;
          border-radius: 10px;
        }
        @media (max-width: 640px) {
          .animate-slide-up {
            animation: slide-up 0.25s ease-out;
          }
        }
      `}</style>
    </div>
  );
};

export default AttendanceModal;